import { Localizable } from '@components/localizable/Localizable'
import Page from '@components/Page/Page'
import Perk from '@components/perk/Perk'
import { TableFilters } from '@components/TableFilters'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { Perk as PerkModel } from '@models/membership/Perk'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getPerks,
  selectPerks,
  setPerkQuery,
} from '@store/membership/PerkReducer'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { Card, Col, Input, Pagination, Row } from 'antd'
import { useEffect } from 'react'
const PerksPage = () => {
  const { isOpen } = useSBRM()

  const dispatch = useAppDispatch()
  const perks = useAppSelector(selectPerks())
  const { query, isLoading } = useAppSelector((state) => state.perk)

  const fetchPerks = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: params.filters,
      search: params.search,
    }

    dispatch(setPerkQuery(newQuery))
    dispatch(getPerks(newQuery))
  }

  const { handleTableChange, handleFiltersChange, params } =
    useTableUrlSync<PerkModel>({ onParamsChange: fetchPerks })

  useEffect(() => {
    if (isOpen) return

    fetchPerks(params)
  }, [isOpen])

  return (
    <Localizable>
      <Page
        permissions={Permission.managePerksMembership}
        pageTitle={{
          title: 'Benefits',
          level: 4,
          badge: { count: query.pagination?.total, overflowCount: 9999999 },
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <TableFilters
              initialValues={{
                name: params.search.name,
                plans: params.filters.plans?.map(Number),
              }}
              onValuesChange={handleFiltersChange}
              left={[]}
              right={[
                {
                  name: 'name',
                  type: 'search',
                  label: 'Search by name',
                  input: <Input placeholder="Search benefit" allowClear />,
                },
                {
                  name: 'plans',
                  type: 'filter',
                  label: 'Plans',
                  input: (
                    <SBAsyncSelect
                      multiple
                      treeSelectProps={{
                        maxTagCount: 'responsive',
                      }}
                      placeholder="Select plans"
                      entity={Entity.membershipPlan}
                      allowClear
                    />
                  ),
                },
                {
                  type: 'button',
                  input: <Localizable.LangSelector />,
                },
                {
                  type: 'button',
                  input: (
                    <SBRM.Actions
                      actions={[SBRMAction.Create]}
                      entity={Entity.perk}
                    />
                  ),
                },
              ]}
            />
          </Col>
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <Col key={i} xs={24} sm={12} md={8} xl={6}>
                  <Card loading />
                </Col>
              ))
            : perks.map((perk) => (
                <Col key={perk.id} xs={24} sm={12} md={8} xl={6}>
                  <Perk.Card id={perk.id} />
                </Col>
              ))}
        </Row>
        {!isLoading && (query.pagination?.total ?? 0) > 0 && (
          <Row justify="end" style={{ marginTop: 24 }}>
            <Pagination
              current={query.pagination?.current}
              pageSize={query.pagination?.pageSize}
              total={query.pagination?.total}
              onChange={(current, pageSize) =>
                handleTableChange({ current, pageSize }, {}, [])
              }
            />
          </Row>
        )}
      </Page>
    </Localizable>
  )
}

export default PerksPage
